import React, { CSSProperties, FunctionComponent, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

// Reusable sidebar popup wrapper: renders its children via a document.body
// portal with a semi-transparent backdrop. Positioning is up to the caller
// via the `style` prop (e.g. { top, left } or { bottom, right }). Prevents
// the popup from being clipped by any sidebar overflow.

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 9998;
`;

const PopupWrap = styled.div`
    position: fixed;
    z-index: 9999;
`;

interface SidebarPortalPopupProps {
    open: boolean;
    onClose: () => void;
    style?: CSSProperties;
    children?: React.ReactNode;
}

const SidebarPortalPopup: FunctionComponent<SidebarPortalPopupProps> = ({ open, onClose, style, children }) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return createPortal(
        <>
            <Backdrop onClick={onClose} />
            {children && (
                <PopupWrap style={style} onMouseDown={(e) => e.stopPropagation()}>
                    {children}
                </PopupWrap>
            )}
        </>,
        document.body
    );
};

export default SidebarPortalPopup;
